import { Box } from "@mui/material";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";

// Pre-heating slot at the LEFT of the visualizer. "stage" variant shows
// a stylised reheating-furnace following an industrial mimic palette
// (dark refractory body, glowing orange interior, small dark slabs
// being heated). Mouth on the right edge at pass-line height connects
// visually with the stands' pass line. "strip" variant is empty — the
// KPI cards to its right are per-stand and don't apply to pre-heating.
interface Props {
	variant: "stage" | "strip";
}

const REFRACTORY_DARK = "#a06b4f"; // softened brown — outer walls
const REFRACTORY_LIGHT = "#c08770"; // lighter terracotta for top edge
const CAVITY_GLOW_HOT = "#fdba74"; // warm peach in the middle
const CAVITY_GLOW_COOL = "#fb923c"; // muted orange at edges
const MOUTH_ACCENT = "#b87354";

export const PreheatingFurnace = ({ variant }: Props) => {
	if (variant === "stage") {
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					// Very subtle warm tint behind the furnace to differentiate
					// the pre-heating area from the stand row without competing.
					bgcolor: "#fff7ed",
					borderRadius: 0.5,
				}}
			>
				<svg
					viewBox="0 0 100 170"
					preserveAspectRatio="xMidYMid meet"
					style={{ width: "100%", height: "100%", display: "block" }}
				>
					<defs>
						<linearGradient id="furnaceWalls" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={REFRACTORY_LIGHT} />
							<stop offset="100%" stopColor={REFRACTORY_DARK} />
						</linearGradient>
						<linearGradient id="cavityGlow" x1="0" y1="0" x2="1" y2="0">
							<stop offset="0%" stopColor={CAVITY_GLOW_COOL} />
							<stop offset="50%" stopColor={CAVITY_GLOW_HOT} />
							<stop offset="100%" stopColor={CAVITY_GLOW_COOL} />
						</linearGradient>
					</defs>

					{/* Outer body — same width as a StandBlock (56) and the same
					    rounded-rect look, but warm refractory palette. */}
					<rect
						x="22"
						y="20"
						width="56"
						height="130"
						rx="4"
						ry="4"
						fill="url(#furnaceWalls)"
						stroke={REFRACTORY_DARK}
						strokeWidth="1.5"
					/>

					{/* Inner cavity — single horizontal glowing band suggesting
					    the heating zone, no inner figures (left intentionally
					    abstract; the cavity itself reads "this is hot inside"). */}
					<rect
						x="28"
						y="76"
						width="44"
						height="18"
						rx="2"
						ry="2"
						fill="url(#cavityGlow)"
					/>

					{/* Mouth — outlet at pass-line height (PASS_LINE_Y = 85). Bridges
					    the gap between the furnace body's right edge (x=78) and the
					    SVG's right edge (x=100), where the pass line picks up. */}
					<line
						x1="78"
						y1="85"
						x2="92"
						y2="85"
						stroke={MOUTH_ACCENT}
						strokeWidth="2"
					/>
					<circle
						cx="92"
						cy="85"
						r="6"
						fill={mesfColors.background.paper}
						stroke={MOUTH_ACCENT}
						strokeWidth="1.6"
					/>
					<circle cx="92" cy="85" r="3" fill={CAVITY_GLOW_HOT} opacity="0.8" />
				</svg>
			</Box>
		);
	}

	// "strip" variant — intentionally empty.
	return <Box sx={{ minHeight: 100 }} />;
};
